import { withErrorHandling, withLoader } from '@/store/thunks/thunkWrappers';
import { setPassword, setUkek } from '@/store/slices/auth/authSlice';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { isAPIError } from '@/store/utils';
import type { LoginDTO, LoginResult, LoginReturn } from '@/types/auth/AuthDTOs';
import { getUserIdFromToken } from '@/store/utils/jwtDecoder';
import { setUkekInStorage } from '@/utils/sessionStorage';

const mapCredentialVerificationResult = (result: LoginResult): LoginReturn => ({
  twoFactorToken: result.twoFactorToken,
  maskedEmail: result.maskedEmail,
  maskedPhone: result.maskedPhone,
});

export const credentialVerification = createAppAsyncThunk<LoginReturn, LoginDTO>(
  'auth/login',
  withErrorHandling(
    withLoader(
      async(credentials, { dispatch, extra: { authService, pocServices: { cryptoService } }, rejectWithValue }) => {
        const normalizedCredentials = { ...credentials, username: credentials.username.toLowerCase() };
        try {
          const result = await authService.login(normalizedCredentials);
          const mappedResult = mapCredentialVerificationResult(result);
          const userId = getUserIdFromToken(mappedResult.twoFactorToken);
          const ukek = await cryptoService.deriveKey(normalizedCredentials.password, normalizedCredentials.username);

          dispatch(setUkek(ukek));
          setUkekInStorage(userId, ukek);
          dispatch(setPassword(normalizedCredentials.password));

          return mappedResult;
        } catch (error) {
          if (isAPIError(error) && error.fieldErrors != null) {
            return rejectWithValue({ fieldErrors: error.fieldErrors });
          }
          throw error;
        }
      }
    )
  )
);
